import { NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, TemplateRef, Type, inject, reflectComponentType } from '@angular/core';
import { ModalRef } from '@celerofinancas/ui-modals';

/**
 * What a modal opened through `ModalService` may carry in `data`.
 */
interface ModalData {
  /**
   * Title rendered in the frame's header.
   */
  headerTitle?: string;
  /**
   * Turns on this layer's frame — see the class docblock.
   */
  framed?: boolean;

  /**
   * Anything else the wrapped content expects — this layer forwards it untouched.
   */
  [key: string]: unknown;
}

/**
 * Package filter modals that come without a close button, by selector.
 *
 * F1 asks for an "X" on every filter modal that does not have one. Ours ("Todos os
 * registros") gets it from the frame below. The "Realizados" one is `ui-cash-flow`'s,
 * draws its own card, and closes only on the backdrop — so it passes through this
 * component unframed, and this list is how it still gets the button.
 *
 * By selector and not by class because the package does not export
 * `BfmCashFlowModalComponent`. `reflectComponentType` is Angular's public API for
 * reading a component's metadata, so this does not depend on package internals.
 */
const FILTER_MODALS_WITHOUT_CLOSE = ['bfm-cash-flow-modal'];

/**
 * Optional frame for modals opened through `ModalService`: title, close button
 * and the card surface.
 *
 * ## Why it exists
 *
 * The default `ModalComponent` in `ui-modals` renders only
 * `<div class="cds-modal-content">` with the content inside, **with no styling at
 * all**: no background, no border, no header. A modal written in the custom layer
 * shows up transparent over the screen — which is what happened to the statement
 * filters modal in its first version.
 *
 * ## Why it is optional
 *
 * This component is registered in `MODAL_COMPONENT_EXTERNAL`, and the token
 * applies to **every** modal opened through `ModalService` — including the
 * `ui-cash-flow` ones, such as "Detalhe do Registro". Those already draw their own
 * card and their own close button; framing them produced a card inside a card and
 * two "x" on the same screen.
 *
 * So the frame is opt-in: whoever opens the modal passes `framed: true` in the
 * data. Without the flag this component is a pass-through — the same behaviour as
 * the default `ModalComponent`, which is what the package modals expect. The
 * alternative would be hiding the package's header and button with CSS, which
 * erases a real affordance (an invisible button is still reachable by keyboard).
 */
@Component({
  selector: 'modal-wrapper',
  standalone: true,
  imports: [NgComponentOutlet, NgTemplateOutlet],
  templateUrl: './modal-wrapper.component.html',
  styleUrls: ['./modal-wrapper.component.scss'],
  host: { '[class.modal--framed]': 'isFramed', '[class.modal--floating-close]': 'needsFloatingClose' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalWrapperComponent {
  /**
   * Modal reference, the source of the content and of the frame flag.
   */
  public readonly modalRef = inject<ModalRef<unknown, ModalData>>(ModalRef);

  /**
   * Whether the caller opted into the card and the header.
   */
  public get isFramed(): boolean {
    return this.modalRef.data?.framed === true;
  }

  /**
   * Whether the content is a package filter modal with no close button of its own —
   * see {@link FILTER_MODALS_WITHOUT_CLOSE}. Only for unframed modals: a framed one
   * already has the "X" in its header.
   */
  public get needsFloatingClose(): boolean {
    const content = this.contentAsComponent;
    const selector = content ? reflectComponentType(content)?.selector : undefined;

    return !this.isFramed && !!selector && FILTER_MODALS_WITHOUT_CLOSE.includes(selector);
  }

  /**
   * Header title, empty when the caller passed none.
   */
  public get title(): string {
    return this.modalRef.data?.headerTitle ?? '';
  }

  /**
   * `ModalRef.content` accepts three shapes — component, template or string — and
   * each one needs a different outlet.
   */
  public get contentAsComponent(): Type<unknown> | null {
    return typeof this.modalRef.content === 'function' ? (this.modalRef.content as Type<unknown>) : null;
  }

  /**
   * Content as a template, when the caller passed a `TemplateRef`.
   */
  public get contentAsTemplate(): TemplateRef<unknown> | null {
    return this.modalRef.content instanceof TemplateRef ? this.modalRef.content : null;
  }

  /**
   * Content as plain text, when the caller passed a string.
   */
  public get contentAsText(): string | null {
    return typeof this.modalRef.content === 'string' ? this.modalRef.content : null;
  }

  /**
   * Closes the modal with no result — what the header's "x" does.
   */
  public close(): void {
    this.modalRef.close();
  }
}
